/**
 * IPSS Last Searches Web Component
 *
 * Attributes:
 *   api-url – last-searches API endpoint
 *   is-mobile – pass mobile arg, by default false
 *
 * Usage:
 *   <ipss-last-searches
 *      animal-type="dog"
 *      api-url="https://tierkrankenversicherungen.check24.de/hkv/hund/ipss/lastsearches/?_=1770991459178"
 *   ></ipss-last-searches>
 */
import { LastSearchItem } from './LastSearchItem.js';
import { ContractItem } from './ContractItem.js';

const MAX_TILES_PER_PAGE = 4;

const STATES = {
    NoLastSearchesOrContracts: 1,
    NotLoggedInWithLastSearchesAndContracts: 2,
    LoggedInOnlyContracts: 3,
    LoggedInOnlyLastSearches: 4,
    LoggedInLastSearchesAndContracts: 5,
};

const FILTERS = {
    lastSearches: 'lastSearches',
    contracts: 'contracts',
    all: 'all',
};

let MOBILE_BREAKPOINT = '(max-width: 767px)';
let isMobile = window.matchMedia(MOBILE_BREAKPOINT);

class IpssLastSearches extends HTMLElement {

    static observedAttributes = ['animal-type', 'api-url'];

    constructor() {
        super();

        this.data = { lastSearches: [], contracts: [] };
        this.state = STATES.NoLastSearchesOrContracts;
        this.currentPage = 0;
        this.currentTab = 'all';
        this.tilesData = [];
        this.filteredTiles = [];
    }

    // constructor
    connectedCallback() {
        this.animalType = this.getAttribute('animal-type') || 'dog';
        this.apiUrl = this.getAttribute('api-url');
        this.isMobile = isMobile.matches;
        if (!this.apiUrl) {
            return;
        }

        this.renderLoading();
        this.fetchData().then(isSuccessfulFetch => {
            if (this.isMobile) {
                this.renderMobile(isSuccessfulFetch);
            } else {
                this.render(isSuccessfulFetch);
            }
            this.dispatchLoadedEvent(isSuccessfulFetch);
        });
    }

    async fetchData() {
        try {
            const response = await fetch(this.apiUrl, { credentials: 'include' });
            if (!response.ok) {
                return false;
            }
            this.data = await response.json();
        } catch (err) {
            return false;
        }
        this.state = this.detectState(this.data);
        this.tilesData = this.buildMappedAndSortedItemsData(this.data);
        this.filteredTiles = this.filterTiles(FILTERS.all);
        this.currentPage = 0;
        return true;
    }

    detectState(data) {
        const hasSearches = data.lastSearches?.length > 0;
        const hasContracts = data.contracts?.length > 0;
        const isLoggedIn = hasContracts || this.hasAttribute('logged-in');

        if (!hasSearches && !hasContracts) {
            return STATES.NoLastSearchesOrContracts;
        }

        if (!isLoggedIn && hasSearches) {
            return STATES.NotLoggedInWithLastSearchesAndContracts;
        }

        if (isLoggedIn && hasSearches && !hasContracts) {
            return STATES.LoggedInOnlyLastSearches;
        }

        if (isLoggedIn && !hasSearches && hasContracts) {
            return STATES.LoggedInOnlyContracts;
        }

        if (isLoggedIn && hasSearches && hasContracts) {
            return STATES.LoggedInLastSearchesAndContracts;
        }

        return STATES.NoLastSearchesOrContracts;
    }

    /**
     * Normalize API data into a flat tile array.
     */
    buildMappedAndSortedItemsData(data) {
        const searches = this.sortResults(data.lastSearches, this.mapLastSearchItem);
        const contracts = this.sortResults(data.contracts, this.mapContractItem);

        if (this.hasLastSearchesAndContracts()) {
            const first3 = searches.slice(0, 3);
            const firstContract = contracts[0];
            const remaining = [
                ...searches.slice(3),
                ...contracts.slice(1),
            ].sort((a, b) => b.updated - a.updated);

            return firstContract
                ? [...first3, firstContract, ...remaining]
                : [...first3, ...remaining];
        }

        // For other states: simple chronological merge
        return [...searches, ...contracts].sort((a, b) => b.updated - a.updated);
    }

    sortResults(data, mapItem) {
        return (data || [])
            .map(s => mapItem(s, this))
            .sort((a, b) => b.updated - a.updated);
    }

    mapLastSearchItem(s, self) {
        return new LastSearchItem(s, self.isMobile);
    }

    mapContractItem(c, self) {
        return new ContractItem(c, self.isMobile);
    }

    filterTiles(filter) {
        if (filter !== FILTERS.all) {
            return this.tilesData.filter(t => t.filter === filter);
        }
        return this.tilesData;
    }

    // =========================================================================
    // RENDERING
    // =========================================================================
    dispatchLoadedEvent(isSuccessfulFetch) {
        this.dispatchEvent(new CustomEvent('ipss-last-searches-loaded', {
            bubbles: true,
            detail: {
                isSuccessfulFetch: isSuccessfulFetch,
                data: this.tilesData
            }
        }));
    }

    renderLoading() {
        this.innerHTML = `<div class="ipss-last-search-loading"></div>`;
    }

    render(isSuccessfulFetch) {
        if (!isSuccessfulFetch) {
            this.innerHTML = `<div class="ipss-error-loading"></div>`;
            return;
        }

        if (this.tilesData.length === 0) {
            this.innerHTML = `<div class="ipss-last-search no-results"></div>`;
            return;
        }

        this.innerHTML = `
            <div class="ipss-last-search">
                <div class="ipss-header">
                    ${this.renderTabs()}
                </div>
                ${this.renderCarousel()}
            </div>`;

        this.bindTabEvents();
        this.bindCarouselEvents();
        this.bindDeleteEvents();
        this.bindTileClickEvents();
        this.renderCallback();
    }

    renderTabs() {
        if (!this.hasLastSearchesAndContracts()) {
            return '';
        }

        if (this.hasOnlyOneTypeOfTiles()) {
            return '';
        }

        return `<div class="ipss-tabs">
                    <button class="ipss-tab ${this.currentTab === FILTERS.lastSearches ? 'is-active' : ''}" data-tab="${FILTERS.lastSearches}">Vergleiche</button>
                    <button class="ipss-tab ${this.currentTab === FILTERS.contracts ? 'is-active' : ''}" data-tab="${FILTERS.contracts}">Verträge</button>
                    <button class="ipss-tab ${this.currentTab === FILTERS.all ? 'is-active' : ''}" data-tab="${FILTERS.all}">alles anzeigen</button>
                </div>`;
    }

    bindTabEvents() {
        if (!this.hasLastSearchesAndContracts()) {
            return;
        }

        this.querySelectorAll('.ipss-tab').forEach(btn => {
            btn.addEventListener('click', () => {
                this.currentTab = btn.dataset.tab;
                this.filteredTiles = this.filterTiles(this.currentTab);
                this.currentPage = 0;
                this.render(true);
            });
        });
    }

    renderMobile(isSuccessfulFetch) {
        if (!isSuccessfulFetch) {
            this.innerHTML = `<div class="ipss-error-loading"></div>`;
            return;
        }

        if (this.tilesData.length === 0) {
            this.innerHTML = `<div class="ipss-last-search no-results--mobile">`;
            return;
        }

        this.innerHTML = `
            <div class="ipss-last-search ipss-last-search--mobile">
                ${this.hasLastSearches() ? `
                    <div class="ipss-mobile-section">
                        <div class="ipss-mobile-section-title">Vergleich fortsetzen</div>
                        <div class="ipss-mobile-carousel-viewport">
                            <div class="ipss-mobile-carousel-track">
                                ${this.renderMobileLastSearchesTiles()}
                            </div>
                        </div>
                    </div>
                ` : ''}
                ${this.hasContracts() ? `
                    <div class="ipss-mobile-section">
                        <div class="ipss-mobile-section-title">Verträge</div>
                        <div class="ipss-mobile-carousel-viewport">
                            <div class="ipss-mobile-carousel-track">
                                ${this.renderMobileContractsTiles()}
                            </div>
                        </div>
                    </div>
                ` : ''}
            </div>`;

        this.bindDeleteEvents();
        this.bindTileClickEvents();
        this.renderMobileCallback();
    }

    get totalPages() {
        return Math.ceil(this.filteredTiles.length / MAX_TILES_PER_PAGE);
    }

    // -------------------------------------------------------------------------
    // Carousel
    // -------------------------------------------------------------------------
    renderCarousel() {
        const tiles = this.filteredTiles;
        if (!tiles.length) {
            return '';
        }

        const showLeft = this.currentPage > 0;
        const showRight = this.currentPage < this.totalPages - 1;

        const pages = [];
        for (let i = 0; i < tiles.length; i += MAX_TILES_PER_PAGE) {
            pages.push(tiles.slice(i, i + MAX_TILES_PER_PAGE));
        }

        const pagesHtml = pages.map(page =>
            `<div class="ipss-carousel-page">${page.map(t => this.renderTile(t)).join('')}</div>`
        ).join('');

        const dotsHtml = this.totalPages > 1
            ? `<div class="ipss-pagination">${
                pages.map((_, idx) =>
                    `<span class="ipss-dot ${idx === this.currentPage ? 'is-active' : ''}" data-page="${idx}"></span>`
                ).join('')
            }</div>`
            : '';

        return `
            <div class="ipss-carousel">
                <button class="ipss-chevron ipss-chevron--prev ${showLeft ? '' : 'is-hidden'}"
                        aria-label="Zurück"></button>
                <div class="ipss-carousel-viewport">
                    <div class="ipss-carousel-track">
                        ${pagesHtml}
                    </div>
                </div>
                <button class="ipss-chevron ipss-chevron--next ${showRight ? '' : 'is-hidden'}"
                        aria-label="Weiter"></button>
            </div>
            ${dotsHtml}`;
    }

    renderTile(tile) {
        if (tile instanceof ContractItem) {
            return this.renderContractTile(tile);
        }

        return this.renderLastSearchTile(tile);
    }

    /**
     * @param {LastSearchItem} tile
     * @returns {string}
     */
    renderLastSearchTile(tile) {
        const fallback = tile.fallbackImage
            ? `onerror="this.onerror=null;this.src='${tile.fallbackImage}'"`
            : '';

        return `<div class="ipss-item" data-delete-url="${tile.deleteUrl || ''}" data-cta-url="${tile.ctaUrl || '#'}" data-category="${tile.categoryType}">
                    <div class="ipps-item-header">
                        <span class="tile-title">Vergleich fortsetzen</span>
                        <span class="activity-status">
                            <span class="timestamp">${tile.timeAgo}</span>
                            <span class="device">${tile.deviceLabel}</span>
                        </span>
                        <a class="ipss-item-delete"></a>
                    </div>
                    <div class="ipss-icon">
                        <img class="icon-rounded" src="${tile.image}" alt="${tile.displayName}" ${fallback}>
                    </div>
                    <div class="ipss-animal-data">
                        <span class="animal-name label"><span class="icon ${this.animalType}"></span>${tile.displayName}</span>
                        <span class="protection label"><span class="icon ${tile.protectionIcon}"></span>${tile.protectionLabel}</span>
                        <span class="age label"><span class="cake-icon"></span>${tile.ageInWeeksToYears}</span>
                    </div>
                    <div class="ipss-tariff-data">
                        <span class="count">${tile.count} Tarife</span>
                        <div class="tariff-price-period">ab
                            <div class="price">${tile.formattedPrice} €</div>
                            <div class="period">${tile.periodLabel}</div>
                        </div>
                    </div>
                    <div class="ipss-item-buttons">
                        <a class="c24Button js24ButtonCompare secondary">${tile.ctaText}</a>
                    </div>
                </div>`;
    }

    /**
     * @param {ContractItem} tile
     * @returns {string}
     */
    renderContractTile(tile) {
        const fallback = tile.fallbackImage
            ? `onerror="this.onerror=null;this.src='${tile.fallbackImage}'"`
            : '';

        return `<div class="ipss-item" data-delete-url="${tile.deleteUrl || ''}" data-cta-url="${tile.ctaUrl || '#'}" data-category="${tile.categoryType}">
                    <div class="ipps-item-header">
                        <span class="tile-title">${tile.title}</span>
                        <span class="activity-status">
                            <span class="timestamp">${tile.timeAgo}</span>
                            <span class="device">${tile.deviceLabel}</span>
                        </span>
                        ${ tile.categoryType !== 'contract' ? '<a class="ipss-item-delete"></a>' : ''}
                    </div>
                    <div class="ipss-provider-logo">
                        <img class="logo-size" src="${tile.providerLogo}" alt="${tile.providerName}" ${fallback}>
                    </div>
                    <div class="ipss-animal-data">
                        <span class="animal-name label"><span class="icon ${this.animalType}"></span>${tile.animalNameOrBreed}</span>
                        <span class="protection label"><span class="icon ${tile.protectionIcon}"></span>${tile.protection}</span>
                        <span class="age label"><span class="cake-icon"></span>${tile.ageInWeeksToYears}</span>
                    </div>
                    <div class="ipss-tariff-data">
                        <span class="ipss-status ${tile.status?.color}">${tile.status?.text}</span>
                        <div class="tariff-price-period">
                            <span class="price">${tile.formattedPrice} €</span>
                            <span class="period">${tile.periodLabel}</span>
                        </div>
                    </div>
                    <div class="ipss-item-buttons">
                        <a class="c24Button js24ButtonCompare secondary">${tile.ctaText}</a>
                    </div>
                </div>`;
    }

    renderMobileLastSearchesTiles() {
        const lastSearches = this.tilesData.filter(t => t instanceof LastSearchItem);
        return lastSearches.map(tile => this.renderMobileLastSearchTile(tile)).join('');
    }

    renderMobileContractsTiles() {
        const contracts = this.tilesData.filter(t => t instanceof ContractItem);
        return contracts.map(tile => this.renderMobileContractTile(tile)).join('');
    }

    renderMobileLastSearchTile(tile) {
        const fallback = tile.fallbackImage
            ? `onerror="this.onerror=null;this.src='${tile.fallbackImage}'"`
            : '';

        return `<div class="ipss-mobile-item" data-cta-url="${tile.ctaUrl || '#'}" data-delete-url="${tile.deleteUrl || ''}" data-category="${tile.categoryType}">
                    <div class="ipss-close-button-wrapper">
                        <a class="ipss-mobile-item-delete"></a>                    
                    </div>
                    <div class="mobile-tile-header">
                        <div class="ipss-mobile-image">
                            <img src="${tile.image}" alt="${tile.displayName}" ${fallback}>
                        </div>
                        <div class="ipss-animal-data">
                            <span class="animal-name label"><span class="icon ${this.animalType}"></span>${tile.displayName}</span>
                            <span class="protection label"><span class="icon ${tile.protectionIcon}"></span>${tile.protectionLabel}</span>
                            <span class="age label"><span class="cake-icon"></span>${tile.ageInWeeksToYears}</span>
                        </div>
                    </div>
                    <div class="mobile-tile-footer">
                        <span>${tile.timeAgo} ${tile.deviceLabel}</span>
                        <span>${tile.count} Tarife ab</span>
                        <span>mtl. <span class="mobile-tile-footer-price">${tile.formattedPrice} €</span></span>
                    </div>
                </div>`
    }

    renderMobileContractTile(tile) {
        const fallback = tile.fallbackImage
            ? `onerror="this.onerror=null;this.src='${tile.fallbackImage}'"`
            : '';

        return `<div class="ipss-mobile-item" data-cta-url="${tile.ctaUrl || '#'}" data-delete-url="${tile.deleteUrl || ''}" data-category="${tile.categoryType}">
                    <div class="ipss-close-button-wrapper">
                        <a class="ipss-mobile-item-delete"></a>                    
                    </div>
                    <div class="mobile-tile-header">
                        <div class="ipss-mobile-image">
                            <img src="${tile.providerLogo}" alt="${tile.providerName}" ${fallback}>
                        </div>
                        <div class="ipss-animal-data">
                            <span class="animal-name label"><span class="icon ${this.animalType}"></span>${tile.animalNameOrBreed}</span>
                            <span class="protection label"><span class="icon ${tile.protectionIcon}"></span>${tile.protection}</span>
                            <span class="age label"><span class="cake-icon"></span>${tile.ageInWeeksToYears}</span>
                        </div>
                    </div>
                    <div class="mobile-tile-footer" data-category="offer">
                        <span class="ipss-status ${tile.status?.color}">${tile.status?.text}</span>
                        <span>mtl. <span class="mobile-tile-footer-price">${tile.formattedPrice} €</span></span>
                    </div>
                </div>`
    }

    hasLastSearches() {
        return this.tilesData.some(t => t instanceof LastSearchItem);
    }

    hasContracts() {
        return this.tilesData.some(t => t instanceof ContractItem);
    }

    bindDeleteEvents() {
        this.addEventListener('click', (e) => {
            const deleteBtn = e.target.closest('.ipss-item-delete, .ipss-mobile-item-delete');
            if (!deleteBtn) return;

            e.preventDefault();
            const tile = deleteBtn.closest('.ipss-item, .ipss-mobile-item');
            if (!tile) return;

            const url = tile.dataset.deleteUrl;
            if (!url) return;

            fetch(url, { credentials: 'include' }).then(response => {
                if (!response.ok) return;

                tile.style.transition = 'opacity 0.3s ease';
                tile.style.opacity = '0';
                tile.addEventListener('transitionend', () => {
                    const idx = this.tilesData.findIndex(t => t.deleteUrl === url);
                    if (idx !== -1) this.tilesData.splice(idx, 1);
                    if (this.tilesData.length === 0) {
                        this.innerHTML = '';
                        if (this.isMobile) {
                            document.querySelectorAll('.hide-on-non-empty-ipss-mobile').forEach(el => {
                                el.style.display = '';
                                el.classList.remove('ipss-collapsing');
                                el.style.height = '';
                                el.style.overflow = '';
                            });
                        } else {
                            const hiddenEl = document.getElementById('hide-on-non-empty-ipss-desktop');
                            if (hiddenEl) {
                                hiddenEl.style.display = '';
                                hiddenEl.classList.remove('ipss-collapsing');
                                hiddenEl.style.height = '';
                                hiddenEl.style.overflow = '';
                            }
                        }
                        return;
                    }
                    this.filteredTiles = this.filterTiles(this.currentTab);

                    if (this.currentPage >= this.totalPages) {
                        this.currentPage = Math.max(0, this.totalPages - 1);
                    }

                    if (this.isMobile) {
                        this.renderMobile(true);
                    } else {
                        this.render(true);
                    }
                }, { once: true });
            });
        });
    }

    bindCarouselEvents() {
        const leftBtn = this.querySelector('.ipss-chevron--prev');
        const rightBtn = this.querySelector('.ipss-chevron--next');

        if (leftBtn) {
            leftBtn.addEventListener('click', () => this.navigate(-1));
        }
        if (rightBtn) {
            rightBtn.addEventListener('click', () => this.navigate(1));
        }

        this.querySelectorAll('.ipss-dot').forEach(dot => {
            dot.addEventListener('click', () => {
                this.currentPage = parseInt(dot.dataset.page, 10);
                this.updateCarousel();
            });
        });

        this.initSwipe();
    }

    bindTileClickEvents() {
        this.addEventListener('click', (e) => {
            if (e.target.closest('.ipss-item-delete, .ipss-mobile-item-delete')) return;

            const tile = e.target.closest('.ipss-item, .ipss-mobile-item');
            if (!tile) return;

            const url = tile.dataset.ctaUrl;
            if (url && url !== '#') {
                window.location.href = url;
            }
        });
    }

    navigate(direction) {
        const next = this.currentPage + direction;
        if (next < 0 || next >= this.totalPages) return;
        this.currentPage = next;
        this.updateCarousel();
    }

    updateCarousel() {
        const viewport = this.querySelector('.ipss-carousel-viewport');
        const track = this.querySelector('.ipss-carousel-track');
        if (track && viewport) {
            const offset = this.currentPage * viewport.offsetWidth;
            track.style.transform = `translateX(-${offset}px)`;
        }

        const left = this.querySelector('.ipss-chevron--prev');
        const right = this.querySelector('.ipss-chevron--next');
        if (left) left.classList.toggle('is-hidden', this.currentPage === 0);
        if (right) right.classList.toggle('is-hidden', this.currentPage >= this.totalPages - 1);

        this.querySelectorAll('.ipss-dot').forEach((dot, idx) => {
            dot.classList.toggle('is-active', idx === this.currentPage);
        });
    }

    initSwipe() {
        const viewport = this.querySelector('.ipss-carousel-viewport');
        if (!viewport) return;

        let startX = 0;
        let deltaX = 0;

        viewport.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            deltaX = 0;
        }, { passive: true });

        viewport.addEventListener('touchmove', (e) => {
            deltaX = e.touches[0].clientX - startX;
        }, { passive: true });

        viewport.addEventListener('touchend', () => {
            if (Math.abs(deltaX) > 50) {
                this.navigate(deltaX > 0 ? -1 : 1);
            }
        });
    }

    hasLastSearchesAndContracts() {
        return [
            STATES.LoggedInLastSearchesAndContracts,
            STATES.NotLoggedInWithLastSearchesAndContracts
        ].includes(this.state)
    }

    hasOnlyOneTypeOfTiles() {
        if (this.hasContracts() && !this.hasLastSearches()) {
            return true;
        }

        return !!(this.hasLastSearches() && !this.hasContracts());
    }

    collapseElement(el) {
        if (!el) return;
        const height = el.scrollHeight;
        el.style.height = height + 'px';
        el.style.overflow = 'hidden';
        el.offsetHeight; // force reflow
        el.classList.add('ipss-collapsing');
        el.style.height = '0';

        el.addEventListener('transitionend', () => {
            el.style.display = 'none';
            el.classList.remove('ipss-collapsing');
        }, { once: true });
    }

    renderCallback() {
        let el = document.getElementById('hide-on-non-empty-ipss-desktop');
        if (el && this.tilesData?.length > 0) {
            this.collapseElement(el);
        }
    }

    renderMobileCallback() {
        if (this.tilesData?.length > 0) {
            document.querySelectorAll('.hide-on-non-empty-ipss-mobile').forEach(el => {
                this.collapseElement(el);
            });
        }
    }
}

customElements.define('ipss-last-searches', IpssLastSearches);
